import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  GitBranch, Plus, Pencil, Trash2, Tag, Clock, Flag, Building2,
  ChevronRight, Save, RotateCcw, Zap, Shield, Package, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

const TYPE_COLORS = {
  major: 'bg-red-500/20 text-red-400 border-red-500/40',
  minor: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
  patch: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
  hotfix: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
};

// ==================== RELEASES TAB ====================
function ReleasesTab() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ version: '', title: '', description: '', changes: [''], affected_modules: [], release_type: 'minor' });

  const fetch = () => {
    axios.get(`${API}/platform/versions/releases`, { headers: getHeaders() })
      .then(r => setReleases(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const handleSave = async () => {
    const payload = { ...form, changes: form.changes.filter(c => c.trim()) };
    try {
      if (editing) {
        await axios.put(`${API}/platform/versions/releases/${editing}`, payload, { headers: getHeaders() });
        toast.success('Release updated');
      } else {
        await axios.post(`${API}/platform/versions/releases`, payload, { headers: getHeaders() });
        toast.success('Release created');
      }
      setDialogOpen(false);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this release note?')) return;
    try {
      await axios.delete(`${API}/platform/versions/releases/${id}`, { headers: getHeaders() });
      toast.success('Deleted');
      fetch();
    } catch { toast.error('Failed'); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ version: '', title: '', description: '', changes: [''], affected_modules: [], release_type: 'minor' });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r.id);
    setForm({ version: r.version, title: r.title, description: r.description, changes: r.changes?.length ? r.changes : [''], affected_modules: r.affected_modules || [], release_type: r.release_type || 'minor' });
    setDialogOpen(true);
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="releases-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">{releases.length} release{releases.length !== 1 ? 's' : ''} tracked</p>
        <Button onClick={openNew} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="add-release-btn">
          <Plus className="h-4 w-4 mr-2" /> New Release
        </Button>
      </div>

      {releases.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Tag className="h-10 w-10 mx-auto mb-3 text-slate-600" />
          <p>No releases tracked yet. Create your first release note.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {releases.map(r => (
            <Card key={r.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-slate-700/50 rounded-lg mt-0.5">
                      <GitBranch className="h-4 w-4 text-violet-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-bold text-white">v{r.version}</span>
                        <Badge className={`text-[10px] border ${TYPE_COLORS[r.release_type] || TYPE_COLORS.minor}`}>
                          {r.release_type}
                        </Badge>
                      </div>
                      <p className="text-sm font-semibold text-white">{r.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{r.description}</p>
                      {r.changes?.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {r.changes.slice(0, 4).map((c, i) => (
                            <li key={`change-${i}`} className="text-xs text-slate-400 flex items-start gap-1.5">
                              <ChevronRight className="h-3 w-3 mt-0.5 text-slate-600 flex-shrink-0" />{c}
                            </li>
                          ))}
                          {r.changes.length > 4 && <li className="text-xs text-slate-500">+{r.changes.length - 4} more</li>}
                        </ul>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[10px] text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(r.released_at).toLocaleDateString()}</span>
                        {r.affected_modules?.map(m => (
                          <Badge key={m} className="text-[10px] bg-slate-700 text-slate-300 border-slate-600 border">{m}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5 text-slate-400" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(r.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Release' : 'New Release'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Version (semver)</Label>
                <Input value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))}
                  placeholder="1.5.0" className="bg-slate-800 border-slate-600 text-white mt-1 font-mono" disabled={!!editing} />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Type</Label>
                <Select value={form.release_type} onValueChange={v => setForm(f => ({ ...f, release_type: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600 z-[100]">
                    <SelectItem value="major" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Major</SelectItem>
                    <SelectItem value="minor" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Minor</SelectItem>
                    <SelectItem value="patch" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Patch</SelectItem>
                    <SelectItem value="hotfix" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Hotfix</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Title</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Pricing Page & Feature Flags" className="bg-slate-800 border-slate-600 text-white mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2} placeholder="Brief summary..." className="bg-slate-800 border-slate-600 text-white mt-1" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-slate-400">Changes</Label>
                <Button variant="ghost" size="sm" className="text-violet-400 h-6 text-xs"
                  onClick={() => setForm(f => ({ ...f, changes: [...f.changes, ''] }))}>
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              {form.changes.map((c, i) => (
                <div key={`module-${i}`} className="flex gap-2 mb-1.5">
                  <Input value={c} onChange={e => { const ch = [...form.changes]; ch[i] = e.target.value; setForm(f => ({ ...f, changes: ch })); }}
                    placeholder="What changed..." className="bg-slate-800 border-slate-600 text-white text-xs h-8" />
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 flex-shrink-0"
                    onClick={() => setForm(f => ({ ...f, changes: f.changes.filter((_, j) => j !== i) }))}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs text-slate-400">Affected Modules (comma-separated)</Label>
              <Input value={form.affected_modules.join(', ')}
                onChange={e => setForm(f => ({ ...f, affected_modules: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                placeholder="hiretrack, recruiteriq, hrms" className="bg-slate-800 border-slate-600 text-white mt-1 text-xs" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700 text-white">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== FEATURE FLAGS TAB ====================
function FeatureFlagsTab() {
  const [flags, setFlags] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantFlags, setTenantFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchFlags = () => {
    axios.get(`${API}/feature-flags/definitions`, { headers: getHeaders() })
      .then(r => setFlags(r.data))
      .catch(() => toast.error('Failed to load flags'))
      .finally(() => setLoading(false));
  };

  const fetchTenants = () => {
    axios.get(`${API}/platform/tenants`, { headers: getHeaders() })
      .then(r => setTenants(r.data))
      .catch(() => {});
  };

  useEffect(() => { fetchFlags(); fetchTenants(); }, []);

  const selectTenant = async (tenantId) => {
    setSelectedTenant(tenantId);
    try {
      const res = await axios.get(`${API}/feature-flags/tenant/${tenantId}`, { headers: getHeaders() });
      const flagMap = {};
      res.data.flags.forEach(f => { flagMap[f.key] = f.enabled; });
      setTenantFlags(flagMap);
    } catch { toast.error('Failed to load tenant flags'); }
  };

  const handleToggle = (key, value) => {
    setTenantFlags(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!selectedTenant) return;
    setSaving(true);
    try {
      await axios.put(`${API}/feature-flags/tenant/${selectedTenant}`, { flags: tenantFlags }, { headers: getHeaders() });
      toast.success('Feature flags saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  const selectedTenantName = tenants.find(t => t.id === selectedTenant)?.name || '';

  return (
    <div className="space-y-5" data-testid="feature-flags-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">{flags.length} feature flags defined</p>
      </div>

      {/* Global flag defaults */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-slate-700 flex items-center gap-2">
            <Flag className="h-4 w-4 text-violet-400" />
            <h3 className="text-white font-bold text-sm">Global Feature Defaults</h3>
            <span className="text-xs text-slate-500 ml-2">These apply to all tenants unless overridden</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-transparent">
                <TableHead className="text-slate-400 w-60">Feature</TableHead>
                <TableHead className="text-slate-400">Module</TableHead>
                <TableHead className="text-slate-400">Description</TableHead>
                <TableHead className="text-slate-400 text-center w-28">Default</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {flags.map(f => (
                <TableRow key={f.key} className="border-slate-700 hover:bg-slate-800/50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-violet-300 font-mono bg-violet-500/10 px-1.5 py-0.5 rounded">{f.key}</code>
                    </div>
                    <p className="text-xs text-white mt-1 font-medium">{f.name}</p>
                  </TableCell>
                  <TableCell>
                    <Badge className="text-[10px] bg-slate-700 text-slate-300 border-slate-600 border">{f.module}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">{f.description}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={`text-[10px] border ${f.default_enabled ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : 'bg-slate-700 text-slate-400 border-slate-600'}`}>
                      {f.default_enabled ? 'ON' : 'OFF'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Per-tenant overrides */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-4 w-4 text-indigo-400" />
            <h3 className="text-white font-bold text-sm">Per-Tenant Overrides</h3>
          </div>

          <div className="flex items-center gap-3 mb-5">
            <Select value={selectedTenant || ''} onValueChange={selectTenant}>
              <SelectTrigger className="w-72 bg-slate-800 border-slate-600 text-white" data-testid="tenant-select">
                <SelectValue placeholder="Select a tenant..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600 z-[100] max-h-60">
                {tenants.filter(t => !t.is_demo).map(t => (
                  <SelectItem key={t.id} value={t.id} className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">
                    {t.name} ({t.slug})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTenant && (
              <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-flags-btn">
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Overrides'}
              </Button>
            )}
          </div>

          {selectedTenant ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {flags.map(f => (
                <div key={f.key} className="flex items-center justify-between p-3 rounded-lg border border-slate-700 bg-slate-900/50">
                  <div>
                    <p className="text-sm text-white font-medium">{f.name}</p>
                    <p className="text-[10px] text-slate-500">{f.module} &middot; default: {f.default_enabled ? 'on' : 'off'}</p>
                  </div>
                  <Switch checked={tenantFlags[f.key] ?? f.default_enabled}
                    onCheckedChange={v => handleToggle(f.key, v)}
                    data-testid={`flag-${f.key}`} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Building2 className="h-8 w-8 mx-auto mb-2 text-slate-600" />
              <p className="text-sm">Select a tenant to configure feature flags</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function SuperAdminVersions() {
  const [tab, setTab] = useState('releases');

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }} data-testid="versions-page">
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Version Control & Feature Flags
        </h1>
        <p className="text-slate-400 mt-1">Track releases, manage changelogs, and control feature rollout per tenant</p>
      </div>

      <div className="flex gap-1 bg-slate-800/50 p-1 rounded-lg w-fit">
        <button onClick={() => setTab('releases')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${tab === 'releases' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          <GitBranch className="h-4 w-4" /> Releases
        </button>
        <button onClick={() => setTab('flags')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${tab === 'flags' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          <Flag className="h-4 w-4" /> Feature Flags
        </button>
      </div>

      {tab === 'releases' && <ReleasesTab />}
      {tab === 'flags' && <FeatureFlagsTab />}
    </div>
  );
}
